import {
  defaultTimezone,
  fromDateToMonth,
  getAllWeeksOfMonth
} from '@weenat/client/dist/core/history'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Text from 'app/src/kit/Text'
import { isEmpty, isEqual, isNil } from 'lodash-es'
import capitalize from 'lodash-es/capitalize'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../Button'
import { IconProps } from '../Icon'
import Icons from '../Icons'
import MenuItem from '../MenuItem'
import { Dropdown, DropdownContent, DropdownTrigger } from '../fields/Select/SelectDropdown'
import CalendarDay from './CalendarDay'
import CalendarGrid from './CalendarGrid'

const DAY_HEIGHT = 42
const DAY_PADDING = 4

interface PeriodExtremeDisplayProps {
  dateString: string
  extreme: 'start' | 'end'
  /** Selection mode */
  variant: 'singleDate' | 'period'
}

const PeriodExtremeDisplay: FC<PeriodExtremeDisplayProps> = ({ dateString, extreme, variant }) => {
  const { t } = useIntl()

  const textColor = 'grayscale.white'

  return (
    <Flex
      $flexDirection='column'
      $gap='xs'
      $backgroundColor='primary.500'
      $borderRadius={'md'}
      $p='md'
      $flex={1}
    >
      <Text $fontSize='sm' $color={textColor}>
        {t(variant === 'period' ? `periods.${extreme}` : 'calendar.date_picked', {
          capitalize: true
        })}
      </Text>
      <Text $fontWeight='bold' $color={textColor}>
        {dateString}
      </Text>
    </Flex>
  )
}

type CalendarValue = [Date, Date] | Date

interface Month {
  index: number
  year: number
}

const now = new Date()

export interface WeenatCalendarProps {
  /** The date at which the calendar should open */
  initialMonth?: Month
  /** Minimum date before which no date can be selected */
  minDate?: Date
  /** Maximum date after which no date can be selected */
  maxDate?: Date
  /** a range that is invalid inside minDate and maxDate */
  invalidRange?: [Date, Date]
  /** Maximum relative date compute from startDate after which no date can be selected */
  relativeMaxDate?: (startDate: Date) => Date
  /** Method called when the period is changed */
  onCancel?: () => void
  /** Method called when the period is changed */
  onChange: (value: CalendarValue) => void
  /** User timezone */
  timezone: string
  /** Currently selected period of time */
  value: CalendarValue
  /** Selection mode */
  variant: 'singleDate' | 'period'
  /** hide period header */
  hidePeriodHeader?: boolean
  /** automatic change */
  autoChange?: boolean
}

const isPeriod = (value: CalendarValue): value is [Date, Date] => {
  return Array.isArray(value)
}

/**
 * Calendar per month
 */
const WeenatCalendar: FC<WeenatCalendarProps> = ({
  initialMonth,
  maxDate,
  relativeMaxDate,
  minDate,
  invalidRange,
  onCancel,
  onChange,
  timezone = defaultTimezone,
  value,
  variant,
  hidePeriodHeader = false,
  autoChange = false
}) => {
  const { t } = useIntl()

  const [currentMonth, setCurrentMonth] = useState<Month>(initialMonth ?? fromDateToMonth(now))

  const [selectedValue, setSelectedValue] = useState<CalendarValue>(value)
  const [displayedValue, setDisplayedValue] = useState<CalendarValue>(value)
  useEffect(() => {
    setSelectedValue(value)
    setDisplayedValue(value)
  }, [value])

  const momentFromMonth = moment()
    .tz(timezone)
    .set('month', currentMonth.index)
    .set('year', currentMonth.year)

  const nextMonth = () => {
    const newDate = momentFromMonth.clone().add(1, 'month').toDate()
    setCurrentMonth(fromDateToMonth(newDate))
  }

  const goToMonth = (date: Date) => {
    setCurrentMonth(fromDateToMonth(date))
  }

  const monthSelection = useDisclosure()
  const yearSelection = useDisclosure()

  const previousMonth = () => {
    const newDate = momentFromMonth.clone().subtract(1, 'month').toDate()
    setCurrentMonth(fromDateToMonth(newDate))
  }

  const handleSelectionChange = useCallback(
    (newDate: Date) => {
      const newMoment = moment.tz(newDate, timezone).startOf('day')

      if (isPeriod(selectedValue)) {
        let newPeriod: [Date, Date] = [
          newMoment.clone().toDate(),
          newMoment.clone().add(1, 'day').toDate()
        ]

        // IF the selected date is after the currently selected period AND isn't inside the current period
        if (
          newMoment.isAfter(moment.tz(selectedValue[0], timezone), 'date') &&
          !newMoment.isBetween(
            moment.tz(selectedValue[0], timezone),
            moment.tz(selectedValue[1], timezone),
            'day',
            '[)'
          )
        ) {
          newPeriod = [selectedValue[0], newPeriod[1]]
        }

        setSelectedValue(newPeriod)
        setDisplayedValue([
          newPeriod[0],
          moment.tz(newPeriod[1], timezone).subtract(1, 'second').toDate()
        ])

        if (autoChange) {
          onChange(newPeriod)
        }
      } else {
        setSelectedValue(newMoment.clone().toDate())
        setDisplayedValue(newMoment.clone().toDate())
      }
    },
    [selectedValue, timezone, autoChange, onChange]
  )

  const daysOfTheMonth = useMemo(() => {
    return getAllWeeksOfMonth({ index: currentMonth.index, year: currentMonth.year, timezone })
  }, [currentMonth.index, currentMonth.year, timezone])

  const iconProps: Partial<IconProps> = {
    $size: 'lg',
    $p: 'md',
    $backgroundColor: `grayscale.100`
  }

  return (
    <Flex $flexDirection='column' height='100%' $width='100%' $gap='md'>
      {/* Selection Header */}
      {!hidePeriodHeader && (
        <Flex $my={1} $alignItems='center' $gap='md'>
          <PeriodExtremeDisplay
            extreme='start'
            variant={variant}
            dateString={moment(isPeriod(displayedValue) ? displayedValue[0] : displayedValue)
              .tz(timezone)
              .format('DD/MM/YYYY')}
          />

          {isPeriod(displayedValue) ? (
            <PeriodExtremeDisplay
              extreme='end'
              variant={variant}
              dateString={moment.tz(displayedValue[1], timezone).format('DD/MM/YYYY')}
            />
          ) : null}
        </Flex>
      )}
      {/* Navigation Header */}
      <Flex $justifyContent='space-between' $alignItems='center'>
        <Flex $gap='sm'>
          <Dropdown
            isVisible={monthSelection.isOpen}
            onVisibleChange={(becomeVisible) =>
              becomeVisible ? monthSelection.open() : monthSelection.close()
            }
          >
            <DropdownTrigger>
              <Text $fontWeight='bold' $textAlign='center' onClick={() => monthSelection.open()}>
                {capitalize(momentFromMonth.clone().format('MMMM'))}
              </Text>
            </DropdownTrigger>
            <DropdownContent>
              <Flex $flexDirection='column' $gap='md' $pb='md'>
                <Text $fontWeight='bold'>{t('calendar.select_month')}</Text>
                {moment.months().map((month, index) => (
                  <MenuItem
                    key={month}
                    onPress={() => {
                      setCurrentMonth({ index, year: currentMonth.year })
                      monthSelection.close()
                    }}
                  >
                    <Flex $p='md'>
                      <Text>{capitalize(month)}</Text>
                    </Flex>
                  </MenuItem>
                ))}
              </Flex>
            </DropdownContent>
          </Dropdown>
          <Dropdown
            isVisible={yearSelection.isOpen}
            onVisibleChange={(becomeVisible) =>
              becomeVisible ? yearSelection.open() : yearSelection.close()
            }
          >
            <DropdownTrigger>
              <Text $fontWeight='bold' $textAlign='center' onClick={() => yearSelection.open()}>
                {capitalize(momentFromMonth.clone().format('YYYY'))}
              </Text>
            </DropdownTrigger>
            <DropdownContent>
              <Flex $flexDirection='column' $gap='md' $pb='md'>
                <Text $fontWeight='bold'>{t('calendar.select_month')}</Text>
                {
                  // Generate a range of year from 2018 to current year
                  Array.from({ length: moment().year() - 2018 + 1 }, (_, i) => 2018 + i).map(
                    (year) => (
                      <MenuItem
                        key={year}
                        onPress={() => {
                          setCurrentMonth({ index: currentMonth.index, year })
                          yearSelection.close()
                        }}
                      >
                        <Flex $p='md'>
                          <Text>{year}</Text>
                        </Flex>
                      </MenuItem>
                    )
                  )
                }
              </Flex>
            </DropdownContent>
          </Dropdown>
        </Flex>
        <Flex $gap='lg'>
          {!isEqual(fromDateToMonth(now), currentMonth) && (
            <Icons.Today
              onPress={() => goToMonth(now)}
              {...iconProps}
              $color='data.protected'
              $backgroundColor='#EDECFE'
            />
          )}
          <Flex $gap='md'>
            <Icons.ArrowRight $rotate={180} onPress={previousMonth} {...iconProps} />
            <Icons.ArrowRight onPress={nextMonth} {...iconProps} />
          </Flex>
        </Flex>
      </Flex>

      {/* Grid */}
      <CalendarGrid dayHeight={DAY_HEIGHT} dayPadding={DAY_PADDING}>
        {daysOfTheMonth.map((day) => {
          const currentMoment = moment.tz(day, timezone)

          let sinceMoment = undefined
          let beforeMoment = undefined
          let isSelected = false

          if (isPeriod(displayedValue)) {
            sinceMoment = moment.tz(displayedValue[0], timezone)
            beforeMoment = moment.tz(displayedValue[1], timezone)

            // Is selected if is the same or is included inside the period
            isSelected = currentMoment.isBetween(sinceMoment, beforeMoment, 'day', '[)')
          } else {
            sinceMoment = moment.tz(displayedValue, timezone).startOf('day')
            beforeMoment = moment.tz(displayedValue, timezone).endOf('day')

            isSelected = currentMoment.isSame(sinceMoment, 'day')
          }

          const isFromAnotherMonth = day.getMonth() !== currentMonth.index

          const isAfterMaxDate = maxDate
            ? currentMoment.isAfter(maxDate)
            : relativeMaxDate
              ? currentMoment.isAfter(relativeMaxDate(sinceMoment.clone().toDate()))
              : false
          const isBeforeMinDate = minDate ? currentMoment.isBefore(minDate) : false

          const isDisabled =
            isBeforeMinDate ||
            isAfterMaxDate ||
            (!isNil(invalidRange) && !isEmpty(invalidRange) && invalidRange.length === 2
              ? currentMoment.isBetween(invalidRange[0], invalidRange[1], undefined, '[]')
              : false)

          const isFirstOfSelection = currentMoment.isSame(sinceMoment, 'day')
          const isLastOfSelection = currentMoment.isSame(
            beforeMoment.clone().add(-1, 'second'), // beforeMoment is exclusive, so we need to subtract one second
            'day'
          )

          return (
            <CalendarDay
              key={currentMoment.unix()}
              day={day}
              height={DAY_HEIGHT}
              isDisabled={isDisabled}
              isSelected={isSelected}
              isFirstOfSelection={isFirstOfSelection}
              isFromAnotherMonth={isFromAnotherMonth}
              isLastOfSelection={isLastOfSelection}
              onPress={() => handleSelectionChange(day)}
              timezone={timezone}
            />
          )
        })}
      </CalendarGrid>
      {!autoChange && (
        <Flex $my='md' $alignItems='center' $justifyContent='flex-end' $gap='md'>
          {!isNil(onCancel) ? (
            <Button importance='sd' onPress={onCancel}>
              {t('actions.cancel')}
            </Button>
          ) : null}
          <Button onPress={() => onChange(selectedValue)}>{t('actions.confirm')}</Button>
        </Flex>
      )}
    </Flex>
  )
}

export default WeenatCalendar
